import React from 'react'

import { ProductTileProps } from '@Domains/Product/Tiles/types'
import { Maybe, PageType, Product, ProductPageTacticFragmentFragment, useProductTacticsQuery } from '@Generated/graphql'
import { getProductFieldValue } from '@Globals/CMS/FieldHelpers'
import { productTileFragmentToProductTile } from '@Globals/CMS/ProductHelpers'
import { useHasIntersected } from '@Globals/Hooks/Intersection'

export type Group = Exclude<keyof NonNullable<Product['tactics']>, '__typename'>
type TacticWithRecommendations = Extract<ProductPageTacticFragmentFragment, { recommendations?: Maybe<Array<{ __typename?: 'Recommendation' }>> }>

export function useProductTactics(
  productID: string,
  group: Group,
) {
  const [ref, hasIntersected] = useHasIntersected('200px')
  const { data, loading } = useProductTacticsQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
    variables: {
      id: productID,
      type: PageType.Product,
    },
    skip: !hasIntersected,
  })

  // @ts-expect-error
  const tactics = getProductFieldValue(data?.page?.fields || [], 'product')?.tactics?.[group] as ProductPageTacticFragmentFragment[] | undefined

  return [
    ref,
    !hasIntersected || loading,
    tactics,
  ] as const
}

const defaultTactics = [null, [] as ProductTileProps[]] as const

export function useProductTacticWithRecommendations(
  productID: string,
  group: Group,
  id: string,
) {
  const [ref, loading, tactics] = useProductTactics(productID, group)

  const [recommendation, products] = React.useMemo(() => {
    if (loading) {
      return defaultTactics
    }

    const tactic = tactics?.find(i => 'id' in i && i.id === id) as TacticWithRecommendations | undefined
    if (!tactic || !tactic.recommendations || tactic.recommendations.length === 0) {
      return defaultTactics
    }

    const topRecommendation = tactic.recommendations[0]
    if (!topRecommendation || !topRecommendation.products || topRecommendation.products.length === 0) {
      return defaultTactics
    }

    const productTiles = topRecommendation?.products?.map(productTileFragmentToProductTile) || []
    return [topRecommendation, productTiles] as const
  }, [loading, tactics, id])

  return [
    ref,
    loading,
    recommendation,
    products,
  ] as const
}
